/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useCallback } from "react";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";
import { getRelativePath } from "@src/utils/routes";
import { logEvent } from "@src/utils/logClient";
import window from "global/window";
import { useFeatureFlagsWithOverrides } from "@src/utils/useFeatureFlag";
import useSearchParams from "@src/utils/useSearchParams";
import { AppDataContext } from "@src/components/AppDataContext";
import DistrictsHelloBar from "@src/components/Header/DistrictsHelloBar";
import { ClassDojoLogoWhite } from "@src/components/Header/ClassDojoLogoWhite";
import {
  Header as MDSHeader,
  Navbar,
  ExpandingMenu,
  IconApple,
  IconIdentificationBadge,
  IconFolder,
  IconStar,
  IconBook,
  IconUserGroup,
  IconDeviceDesktop,
  IconLock,
  IconNews,
  IconArrowLeft,
  Button,
  Headline,
  Tag,
} from "@web-monorepo/mds";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import Translate from "@src/components/translation/Translate";
import LoginPart from "../partials/header/LoginPart";
import isMobile from "@src/utils/isMobile";
import { navigate } from "gatsby";
import * as S from "./styles";
import HeaderLanguagePicker from "./HeaderLanguagePicker";
import { getSparksSignupUrl, SPARKS_SIGNUP_URL } from "@src/utils/sparks/getSignupUrl";

type HeaderProps = {
  showNavBanner: boolean;
  inSchools: boolean;
  hideNav: boolean;
  unpinHeader: boolean;
  closeBannerFunction: () => void;
  handleIsUserInAudience: (newValue: boolean) => void;
  simplifiedHeader?: boolean;
  // Page opts into a transparent nav (over its own hero background) that turns solid
  // once the user scrolls past TRANSPARENT_HERO_SCROLL_THRESHOLD_PX.
  transparentHero?: boolean;
  // Whitens the logo/nav labels while the nav is transparent — needed over a dark hero
  // (/school-leaders), but not over a light one (/teachers, /families), which keeps the
  // default dark content colors. Ignored unless `transparentHero` is set.
  transparentHeroLightContent?: boolean;
  // Swaps the ClassDojo logo/CTA for the Dojo Sparks lockup + "Free week" CTA, and scopes
  // the header to the `brand-sparks` color theme (see button.styles' Brands section).
  sparksBrand?: boolean;
};

const TRANSPARENT_HERO_SCROLL_THRESHOLD_PX = 80;

const MenuBrandIcon = ({ src }: { src: string }) => {
  return <img src={src} alt="" width={32} height={32} className="h-full object-contain w-full" />;
};

const HeaderComponent: React.FC<HeaderProps> = ({
  hideNav,
  unpinHeader,
  handleIsUserInAudience,
  simplifiedHeader,
  transparentHero,
  transparentHeroLightContent,
  sparksBrand,
}) => {
  const [downloadButtonConfig, setDownloadButtonConfig] = useState<{
    href: string;
    translationPath: string;
  } | null>(null);

  const [sparksSignupUrl, setSparksSignupUrl] = useState(SPARKS_SIGNUP_URL);
  useEffect(() => {
    if (!sparksBrand) return;
    setSparksSignupUrl(getSparksSignupUrl());
  }, [sparksBrand]);

  const handleSparksCtaClick = () => {
    logEvent({ eventValue: window.location.href, eventName: "web.external_page.navbar.sparks_free_week.tap" });
  };

  const { showModal } = useContext(ModalContext);
  const showTeacherSignupModal = () => showModal(ModalType.TeacherSignupForm);
  const showSignupModal = () => showModal(ModalType.Signup);

  const teacherSignupParam = useSearchParams("teacher-signup");
  const generalSignupParam = useSearchParams("create-account");
  const modalContext = useContext(ModalContext);
  const featureFlags = useFeatureFlagsWithOverrides();
  const { getIsCountryUS } = useContext(TranslationContext);
  const isCountryUS = getIsCountryUS();

  const {
    data: { loadingSession, type },
  } = useContext(AppDataContext);

  const [scrolledPastThreshold, setScrolledPastThreshold] = useState(false);
  useEffect(() => {
    if (!transparentHero) return;

    const onScroll = () => setScrolledPastThreshold(window.pageYOffset > TRANSPARENT_HERO_SCROLL_THRESHOLD_PX);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparentHero]);
  const isTransparent = !!transparentHero && !scrolledPastThreshold;

  // The mobile menu panel is always opaque white when open (MDS's own behavior), so the
  // logo/icons/nav text need to fall back to their normal dark colors while it's open even
  // though the header itself is still "transparent".
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const showWhiteTheme = isTransparent && !!transparentHeroLightContent && !mobileMenuOpen;
  const whiteThemeVar = showWhiteTheme ? ({ "--content-primary": "white" } as React.CSSProperties) : undefined;

  // defining audience for banner
  useEffect(() => {
    if (!loadingSession && type === undefined) {
      handleIsUserInAudience(true);
    }
  }, [loadingSession, type]);

  useEffect(() => {
    if (teacherSignupParam[0] !== null) showTeacherSignupModal();
    if (generalSignupParam[0] !== null) showSignupModal();
  }, []);

  // Detect mobile device and set download button config
  useEffect(() => {
    if (isMobile().apple.tablet) {
      setDownloadButtonConfig({
        href: "https://itunes.apple.com/us/app/classdojo/id552602056?mt=8",
        translationPath: "header.nav.get_ipad_app",
      });
    } else if (isMobile().apple.phone) {
      setDownloadButtonConfig({
        href: "https://itunes.apple.com/us/app/classdojo/id552602056?mt=8",
        translationPath: "header.nav.get_iphone_app",
      });
    } else if (isMobile().android.device) {
      setDownloadButtonConfig({
        href: "https://play.google.com/store/apps/details?id=com.classdojo.android",
        translationPath: "header.nav.get_android_app",
      });
    }
  }, []);

  function openLoginModal() {
    logEvent({ eventValue: window.location.href, eventName: "web.external_page.navbar.log_in.tap" });
    modalContext.showModal(ModalType.Login);
  }

  function openSignupModal() {
    logEvent({
      eventValue: window.location.href,
      eventName: "web.external_page.navbar.sign_up.tap",
      metadata: featureFlags,
    });
    modalContext.showModal(ModalType.Signup);
  }

  // Helper function to handle clicks for links with event tracking and navigation
  const handleNavClick = useCallback((eventName: string, href: string) => {
    return (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      logEvent({
        eventValue: window.location.href,
        eventName,
      });

      // Use Gatsby navigation for internal links, regular navigation for external
      if (href.startsWith("http")) {
        window.location.href = href;
      } else {
        navigate(href);
      }
    };
  }, []);

  // Helper to create translated text node
  const getTranslatedText = (path: string) => {
    return <Translate path={path} />;
  };

  if (simplifiedHeader) {
    return (
      <S.Nav role="navigation" unpinHeader={unpinHeader}>
        <S.HeaderContainer height="100%" simplifiedHeader={simplifiedHeader}>
          <a href={getRelativePath("/")}>
            <S.Logo src="https://static.classdojo.com/img/classdojo-light.svg" alt="ClassDojo" />
          </a>
        </S.HeaderContainer>
      </S.Nav>
    );
  }

  return (
    <>
      <DistrictsHelloBar />
      <MDSHeader
        className={`${isTransparent ? "bg-transparent shadow-none" : "bg-surface shadow-sm"} ${
          sparksBrand ? "brand-sparks" : ""
        }`}
        onMenuOpenChange={setMobileMenuOpen}
      >
        <div className="group relative" css={{ flex: 1, minWidth: 154 }}>
          {sparksBrand ? (
            <>
              <a
                href="/sparks"
                onClick={handleNavClick("web.external_page.navbar.sparks_logo.tap", "/sparks")}
                className="inline-flex items-center gap-x-8"
              >
                <img
                  src="https://static.classdojo.com/mds/icon-sparks.svg"
                  alt=""
                  width={40}
                  height={40}
                  className="shrink-0"
                />
                <Headline as="span" size="7" weight="bold" className="text-content-primary whitespace-nowrap">
                  Dojo Sparks
                </Headline>
              </a>
              <a
                href={getRelativePath("/")}
                onClick={handleNavClick("web.external_page.navbar.back_to_classdojo.tap", getRelativePath("/"))}
                className="absolute left-0 top-full z-1 mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-xs bg-ink-800 py-4 pl-4 pr-8 opacity-0 shadow-sm transition-opacity duration-150 group-hover:opacity-100"
              >
                <IconArrowLeft className="size-16 text-content-light" />
                <span className="text-caption text-content-light">Back to ClassDojo</span>
              </a>
            </>
          ) : (
            <a
              href={getRelativePath("/")}
              css={{ display: "inline-block", position: "relative", width: 154, height: 48 }}
            >
              <ClassDojoLogoWhite
                className={`absolute inset-0 transition-opacity duration-250 ${
                  showWhiteTheme ? "opacity-100" : "opacity-0"
                }`}
              />
              <img
                src="https://static.classdojo.com/img/classdojo-logo.svg"
                alt="ClassDojo"
                width={154}
                height={48}
                className={`transition-opacity duration-250 ${showWhiteTheme ? "opacity-0" : "opacity-100"}`}
              />
            </a>
          )}
        </div>

        {!hideNav && (
          <>
            {/* Desktop Navigation */}
            {/* Whitens the trigger row's labels/chevrons for the transparent theme. Menu
                dropdowns render inline here (not via a real portal to document.body), so
                without the reset below they'd inherit the white too — MDS's MenuItem hover
                and default icon colors are also driven by --content-primary, which would
                turn invisible-on-white instead of just the trigger row. */}
            <div
              css={
                showWhiteTheme
                  ? {
                      "--content-primary": "white",
                      "[role='menu'], [role='menuitem']": { "--content-primary": "var(--neutral-900)" },
                    }
                  : undefined
              }
            >
              <Navbar className="max-[870px]:hidden">
                {isCountryUS && (
                  <>
                    {/* Products Menu */}
                    <Navbar.Menu id="products-menu" label={getTranslatedText("layouts.main.products") as any}>
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_products_classdojo.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_products_classdojo.description") as any}
                        href="/"
                        onClick={handleNavClick("web.external_page.navbar.products.classdojo.tap", "/")}
                        icon={<MenuBrandIcon src="https://static.classdojo.com/mds/icon-dojo.svg" />}
                      />
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_products_plus.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_products_plus.description") as any}
                        href="/plus/"
                        onClick={handleNavClick("web.external_page.navbar.products.plus.tap", "/plus/")}
                        icon={<MenuBrandIcon src="https://static.classdojo.com/mds/icon-plus.svg" />}
                      />
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_products_dojo_islands.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_products_dojo_islands.description") as any}
                        href="/dojo-islands"
                        onClick={handleNavClick("web.external_page.navbar.products.islands.tap", "/dojo-islands")}
                        icon={<MenuBrandIcon src="https://static.classdojo.com/mds/icon-islands.svg" />}
                      />
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_products_dojo_tutor.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_products_dojo_tutor.description") as any}
                        href="https://tutor.classdojo.com/?utm_source=newnav"
                        onClick={handleNavClick(
                          "web.external_page.navbar.products.tutor.tap",
                          "https://tutor.classdojo.com/?utm_source=newnav",
                        )}
                        icon={<MenuBrandIcon src="https://static.classdojo.com/mds/icon-tutor.svg" />}
                      />
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_products_dojo_sparks.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_products_dojo_sparks.description") as any}
                        href="/sparks"
                        onClick={handleNavClick("web.external_page.navbar.products.sparks.tap", "/sparks")}
                        icon={<MenuBrandIcon src="https://static.classdojo.com/mds/icon-sparks.svg" />}
                      />
                    </Navbar.Menu>

                    {/* Schools Menu */}
                    <Navbar.Menu id="schools-menu" label={getTranslatedText("layouts.main.schools") as any}>
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_schools_teachers.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_schools_teachers.description") as any}
                        href="/teachers/"
                        onClick={handleNavClick("web.external_page.navbar.schools.teachers.tap", "/teachers/")}
                        icon={<IconApple />}
                      />
                      <Navbar.MenuItem
                        label={
                          (
                            <span className="inline-flex items-center gap-6">
                              {getTranslatedText("layouts.main.submenu_schools_leaders.cta")}
                              <Tag size="xs" color="purple">
                                <Translate path="layouts.main.submenu_schools_leaders.tag" fallback="New" />
                              </Tag>
                            </span>
                          ) as any
                        }
                        description={getTranslatedText("layouts.main.submenu_schools_leaders.description") as any}
                        href="/school-leaders/"
                        onClick={handleNavClick("web.external_page.navbar.schools.leaders.tap", "/school-leaders/")}
                        icon={<IconIdentificationBadge />}
                      />
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_schools_resources.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_schools_resources.description") as any}
                        href="/resources/"
                        onClick={handleNavClick("web.external_page.navbar.schools.resources.tap", "/resources/")}
                        icon={<IconFolder />}
                      />
                    </Navbar.Menu>

                    {/* Districts Menu */}
                    <Navbar.Menu id="districts-menu" label={getTranslatedText("layouts.main.districts") as any}>
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_districts_overview.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_districts_overview.description") as any}
                        href="/districts/"
                        onClick={handleNavClick("web.external_page.navbar.districts.overview.tap", "/districts/")}
                        icon={<IconStar />}
                      />
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_districts_roles.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_districts_roles.description") as any}
                        href="/districts/roles/"
                        onClick={handleNavClick("web.external_page.navbar.districts.roles.tap", "/districts/roles/")}
                        icon={<IconUserGroup />}
                      />
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_districts_features.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_districts_features.description") as any}
                        href="/districts/solutions/"
                        onClick={handleNavClick(
                          "web.external_page.navbar.districts.features.tap",
                          "/districts/solutions/",
                        )}
                        icon={<IconDeviceDesktop />}
                      />
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_districts_privacy.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_districts_privacy.description") as any}
                        href="/privacy-and-security/"
                        onClick={handleNavClick(
                          "web.external_page.navbar.districts.privacy.tap",
                          "/privacy-and-security/",
                        )}
                        icon={<IconLock />}
                      />
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_districts_stories.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_districts_stories.description") as any}
                        href="/districts/customer-stories/"
                        onClick={handleNavClick(
                          "web.external_page.navbar.districts.stories.tap",
                          "/districts/customer-stories/",
                        )}
                        icon={<IconBook />}
                      />
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_districts_blog.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_districts_blog.description") as any}
                        href="https://essential.classdojo.com/?utm_source=newnav"
                        onClick={handleNavClick(
                          "web.external_page.navbar.districts.blog.tap",
                          "https://essential.classdojo.com/?utm_source=newnav",
                        )}
                        icon={<IconNews />}
                      />
                    </Navbar.Menu>

                    {/* Families Link */}
                    <Navbar.Link
                      href="/families/"
                      onClick={handleNavClick("web.external_page.navbar.families.tap", "/families/")}
                    >
                      {getTranslatedText("layouts.main.families")}
                    </Navbar.Link>
                  </>
                )}

                {!isCountryUS && (
                  <>
                    {/* Teachers Link */}
                    <Navbar.Link
                      href={getRelativePath("/teachers/")}
                      onClick={handleNavClick("web.external_page.navbar.teachers.tap", getRelativePath("/teachers/"))}
                    >
                      {getTranslatedText("layouts.main.teachers")}
                    </Navbar.Link>

                    {/* Schools Link */}
                    <Navbar.Link
                      href={getRelativePath("/schools/")}
                      onClick={handleNavClick("web.external_page.navbar.schools.tap", getRelativePath("/schools/"))}
                    >
                      {getTranslatedText("layouts.main.schools")}
                    </Navbar.Link>

                    {/* Resources Menu */}
                    <Navbar.Menu id="resources-menu" label={getTranslatedText("layouts.main.support.resources") as any}>
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_training.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_training.description") as any}
                        href="/training/"
                        onClick={handleNavClick("web.external_page.navbar.training.tap", "/training/")}
                      />
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_resources.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_resources.description") as any}
                        href="/resources/"
                        onClick={handleNavClick("web.external_page.navbar.resources.tap", "/resources/")}
                      />
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_big_ideas.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_big_ideas.description") as any}
                        href="https://ideas.classdojo.com/?utm_source=newnav"
                        onClick={handleNavClick(
                          "web.external_page.navbar.big_ideas.tap",
                          "https://ideas.classdojo.com/?utm_source=newnav",
                        )}
                      />
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_activity_corner.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_activity_corner.description") as any}
                        href="/activity-corner/"
                        onClick={handleNavClick("web.external_page.navbar.activity_corner.tap", "/activity-corner/")}
                      />
                      <Navbar.MenuItem
                        label={getTranslatedText("layouts.main.submenu_sl_resources.cta") as any}
                        description={getTranslatedText("layouts.main.submenu_sl_resources.description") as any}
                        href="/resources/?tab=school-leaders"
                        onClick={handleNavClick(
                          "web.external_page.navbar.schoolleader_resources.tap",
                          "/resources/?tab=school-leaders",
                        )}
                      />
                    </Navbar.Menu>
                  </>
                )}
              </Navbar>
            </div>

            {/* Desktop Actions + Mobile Toggle */}
            <MDSHeader.Actions>
              <ul
                css={{ alignItems: "center", listStyle: "none", margin: 0, padding: 0 }}
                className="max-[870px]:hidden flex gap-x-md"
              >
                <HeaderLanguagePicker light={showWhiteTheme} />
                <LoginPart
                  openLoginModal={openLoginModal}
                  openSignupModal={openSignupModal}
                  context={showWhiteTheme ? "onAccentDark" : "default"}
                  signupOverride={
                    sparksBrand
                      ? { label: "Free week", href: sparksSignupUrl, onClick: handleSparksCtaClick }
                      : undefined
                  }
                />
              </ul>
              {downloadButtonConfig && (
                <div className="max-[870px]:hidden">
                  <div css={{ padding: "15px 10px" }}>
                    <Button
                      variant="primary"
                      size="s"
                      onClick={() => {
                        window.location.href = downloadButtonConfig.href;
                      }}
                    >
                      <Translate path={downloadButtonConfig.translationPath} />
                    </Button>
                  </div>
                </div>
              )}
              <MDSHeader.MobileMenuToggle style={whiteThemeVar} />
            </MDSHeader.Actions>

            {/* Mobile Menu */}
            <MDSHeader.MobileMenu>
              <div>
                {isCountryUS && (
                  <>
                    {/* Products ExpandingMenu */}
                    <ExpandingMenu value="products">
                      <ExpandingMenu.Trigger>{getTranslatedText("layouts.main.products")}</ExpandingMenu.Trigger>
                      <ExpandingMenu.Content>
                        <ExpandingMenu.Item
                          href="/"
                          onClick={handleNavClick("web.external_page.navbar.products.classdojo.tap", "/")}
                        >
                          {getTranslatedText("layouts.main.submenu_products_classdojo.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/plus/"
                          onClick={handleNavClick("web.external_page.navbar.products.plus.tap", "/plus/")}
                        >
                          {getTranslatedText("layouts.main.submenu_products_plus.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/dojo-islands"
                          onClick={handleNavClick("web.external_page.navbar.products.islands.tap", "/dojo-islands")}
                        >
                          {getTranslatedText("layouts.main.submenu_products_dojo_islands.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="https://tutor.classdojo.com/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.products.tutor.tap",
                            "https://tutor.classdojo.com/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_products_dojo_tutor.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/sparks"
                          onClick={handleNavClick("web.external_page.navbar.products.sparks.tap", "/sparks")}
                        >
                          {getTranslatedText("layouts.main.submenu_products_dojo_sparks.cta")}
                        </ExpandingMenu.Item>
                      </ExpandingMenu.Content>
                    </ExpandingMenu>

                    {/* Schools ExpandingMenu */}
                    <ExpandingMenu value="schools">
                      <ExpandingMenu.Trigger>{getTranslatedText("layouts.main.schools")}</ExpandingMenu.Trigger>
                      <ExpandingMenu.Content>
                        <ExpandingMenu.Item
                          href="/teachers/"
                          onClick={handleNavClick("web.external_page.navbar.schools.teachers.tap", "/teachers/")}
                        >
                          {getTranslatedText("layouts.main.submenu_schools_teachers.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/school-leaders/"
                          onClick={handleNavClick("web.external_page.navbar.schools.leaders.tap", "/school-leaders/")}
                        >
                          <span className="inline-flex items-center gap-6">
                            {getTranslatedText("layouts.main.submenu_schools_leaders.cta")}
                            <Tag size="xs" color="purple">
                              <Translate path="layouts.main.submenu_schools_leaders.tag" fallback="New" />
                            </Tag>
                          </span>
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/resources/"
                          onClick={handleNavClick("web.external_page.navbar.schools.resources.tap", "/resources/")}
                        >
                          {getTranslatedText("layouts.main.submenu_schools_resources.cta")}
                        </ExpandingMenu.Item>
                      </ExpandingMenu.Content>
                    </ExpandingMenu>

                    {/* Districts ExpandingMenu */}
                    <ExpandingMenu value="districts">
                      <ExpandingMenu.Trigger>{getTranslatedText("layouts.main.districts")}</ExpandingMenu.Trigger>
                      <ExpandingMenu.Content>
                        <ExpandingMenu.Item
                          href="/districts/"
                          onClick={handleNavClick("web.external_page.navbar.districts.overview.tap", "/districts/")}
                        >
                          {getTranslatedText("layouts.main.submenu_districts_overview.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/districts/roles/"
                          onClick={handleNavClick("web.external_page.navbar.districts.roles.tap", "/districts/roles/")}
                        >
                          {getTranslatedText("layouts.main.submenu_districts_roles.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/districts/solutions/"
                          onClick={handleNavClick(
                            "web.external_page.navbar.districts.features.tap",
                            "/districts/solutions/",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_districts_features.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/privacy-and-security/"
                          onClick={handleNavClick(
                            "web.external_page.navbar.districts.privacy.tap",
                            "/privacy-and-security/",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_districts_privacy.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/districts/customer-stories/"
                          onClick={handleNavClick(
                            "web.external_page.navbar.districts.stories.tap",
                            "/districts/customer-stories/",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_districts_stories.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="https://essential.classdojo.com/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.districts.blog.tap",
                            "https://essential.classdojo.com/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_districts_blog.cta")}
                        </ExpandingMenu.Item>
                      </ExpandingMenu.Content>
                    </ExpandingMenu>

                    {/* Families Link */}
                    <ExpandingMenu.Link
                      href="/families/"
                      onClick={handleNavClick("web.external_page.navbar.families.tap", "/families/")}
                      className="items-center justify-between cursor-pointer transition-colors duration-150 ease-out-quad border-b-1 border-border-decorative text-left"
                    >
                      {getTranslatedText("layouts.main.families")}
                    </ExpandingMenu.Link>
                  </>
                )}

                {!isCountryUS && (
                  <>
                    {/* Teachers Link */}
                    <ExpandingMenu.Link
                      href={getRelativePath("/teachers/")}
                      onClick={handleNavClick("web.external_page.navbar.teachers.tap", getRelativePath("/teachers/"))}
                      className="items-center justify-between cursor-pointer transition-colors duration-150 ease-out-quad border-b-1 border-border-decorative text-left"
                    >
                      {getTranslatedText("layouts.main.teachers")}
                    </ExpandingMenu.Link>

                    {/* Schools Link */}
                    <ExpandingMenu.Link
                      href={getRelativePath("/schools/")}
                      onClick={handleNavClick("web.external_page.navbar.schools.tap", getRelativePath("/schools/"))}
                      className="items-center justify-between cursor-pointer transition-colors duration-150 ease-out-quad border-b-1 border-border-decorative text-left"
                    >
                      {getTranslatedText("layouts.main.schools")}
                    </ExpandingMenu.Link>

                    {/* Resources ExpandingMenu */}
                    <ExpandingMenu value="resources">
                      <ExpandingMenu.Trigger>
                        {getTranslatedText("layouts.main.support.resources")}
                      </ExpandingMenu.Trigger>
                      <ExpandingMenu.Content>
                        <ExpandingMenu.Item
                          href="/training/"
                          onClick={handleNavClick("web.external_page.navbar.training.tap", "/training/")}
                        >
                          {getTranslatedText("layouts.main.submenu_training.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/resources/"
                          onClick={handleNavClick("web.external_page.navbar.resources.tap", "/resources/")}
                        >
                          {getTranslatedText("layouts.main.submenu_resources.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="https://ideas.classdojo.com/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.big_ideas.tap",
                            "https://ideas.classdojo.com/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_big_ideas.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/activity-corner/"
                          onClick={handleNavClick("web.external_page.navbar.activity_corner.tap", "/activity-corner/")}
                        >
                          {getTranslatedText("layouts.main.submenu_activity_corner.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/resources/?tab=school-leaders"
                          onClick={handleNavClick(
                            "web.external_page.navbar.schoolleader_resources.tap",
                            "/resources/?tab=school-leaders",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_sl_resources.cta")}
                        </ExpandingMenu.Item>
                      </ExpandingMenu.Content>
                    </ExpandingMenu>
                  </>
                )}
              </div>

              <MDSHeader.MobileMenuFooter>
                <MDSHeader.Actions className="flex flex-col gap-y-16">
                  <div className="w-full">
                    <HeaderLanguagePicker variant="mobile" />
                  </div>
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      listStyle: "none",
                      margin: 0,
                      gap: 16,
                      padding: 0,
                      width: "100%",
                    }}
                  >
                    <LoginPart
                      openLoginModal={openLoginModal}
                      openSignupModal={openSignupModal}
                      signupOverride={
                        sparksBrand
                          ? { label: "Free week", href: sparksSignupUrl, onClick: handleSparksCtaClick }
                          : undefined
                      }
                    />
                  </div>
                  <div css={{ width: "100%", display: "flex" }}>
                    {downloadButtonConfig && (
                      <Button
                        variant="secondary"
                        size="s"
                        className="grow md:grow-0"
                        onClick={() => {
                          window.location.href = downloadButtonConfig.href;
                        }}
                      >
                        <Translate path={downloadButtonConfig.translationPath} />
                      </Button>
                    )}
                  </div>
                </MDSHeader.Actions>
              </MDSHeader.MobileMenuFooter>
            </MDSHeader.MobileMenu>
          </>
        )}
      </MDSHeader>
    </>
  );
};

export default HeaderComponent;
